import { Label } from "@radix-ui/react-label";
import { useId, type ComponentProps } from "react";
import { useFormContext, type FieldValues, type Path } from "react-hook-form";
import { cn } from "@/lib/utils";
import { Textarea } from "@/components/ui/textarea";
import RequiredMark from "./RequiredMark";
import { useSchema } from "@/v2/hooks/SchemaContext";
import { ZodObject } from "zod";

type FormFieldBaseProps = ComponentProps<typeof Textarea>;

interface ArTextAreaFormFieldProps<
    T extends FieldValues,
> extends FormFieldBaseProps {
    name: Path<T>;
    label: string;
    labelAlign?: "items-end" | "items-start" | "items-center";
}

export function ArTextAreaFormField<T extends FieldValues>({
    label,
    name,
    placeholder,
    labelAlign,
    ...rest
}: ArTextAreaFormFieldProps<T>) {
    const {
        register,
        formState: { errors },
    } = useFormContext<T>();

    const error = errors[name as keyof typeof errors];
    const id = useId();
    const schema = useSchema();

    const requiredCheck = (schema as ZodObject<any>)?.shape?.[name]?.safeParse(
        "",
    );

    const isRequired = !requiredCheck.success;

    return (
        <div className={cn("flex flex-col", labelAlign && labelAlign)}>
            <Label
                htmlFor={id}
                className={cn(
                    "mb-1 text-sm font-medium",
                    error && "text-destructive",
                )}
            >
                {label}
                {isRequired && <RequiredMark />}
            </Label>
            <Textarea
                id={id}
                placeholder={placeholder}
                className={cn(
                    "min-h-[100px]",
                    error &&
                        "border-destructive focus-visible:ring-destructive",
                )}
                {...register(name)}
                {...rest}
            />
            {error && (
                <p id={`${id}-error`} className="mt-1 text-xs text-destructive">
                    {error.message as string}
                </p>
            )}
        </div>
    );
}
